/**
 * Facciata stateful sopra il nucleo di `configurator.ts`.
 *
 * {@link MotornetEngine} mantiene i "pick" dell'utente e le scelte OR, e a ogni
 * mutazione ricostruisce l'intera configurazione ricalcolandone catene, domande
 * aperte, domini e prezzi. Espone un'API ad alto livello (`select`, `deselect`,
 * `setChoices`, interrogazioni sui domini e sui vincoli) usata dal livello di
 * risoluzione/anteprima e dalla UI.
 */
import {
	collectFormulaOrGroups,
	collectVincoloMap,
	deselectAccessory,
	flattenAccessories,
	selectAccessoryWithChoices,
	totalAccessoriesPrice,
	type FormulaOrGroup,
	type OrChoice,
	type WorkingAccessory,
} from './configurator';
import type {
	MotornetAccessoriesResponse,
	MotornetTipoVincolo,
} from 'src/motornet/types';
import type { Domain, OptionId } from './domain';

export type { Domain } from './domain';
export type { FormulaOrGroup, OrChoice } from './configurator';

/** Riga del dettaglio prezzo di un accessorio selezionato. */
export interface PriceLine {
	id: OptionId;
	code: string;
	retail: number;
	base: number;
	requiredBy: OptionId | null;
}

/**
 * Stato sintetico della configurazione: le scelte esplicite (`picks`), l'intera
 * configurazione risultante (`configuration`, inclusi i derivati, esclusi i
 * serie) e i totali degli accessori (lordo/netto).
 */
export interface ConfigState {
	picks: OptionId[];
	configuration: OptionId[];
	price: { retail: number; base: number };
}

/**
 * Engine di configurazione con stato. Costruire tramite {@link MotornetEngine.create};
 * `select` / `deselect` / `setChoices` mutano lo stato e restituiscono il nuovo
 * {@link ConfigState}, mentre i metodi di sola lettura interrogano l'ultima
 * ricostruzione.
 */
export class MotornetEngine {
	/** Scelte esplicite dell'utente, nell'ordine di selezione. */
	private picks: OptionId[] = [];
	/** Copia di lavoro degli accessori, ricostruita a ogni `rebuild`. */
	private working: WorkingAccessory[] = [];
	/** Risposte ai nodi OR (domande) delle formule. */
	private choices: OrChoice[] = [];
	/** Domande aperte/risolte di ciascuna catena, indicizzate per pick capostipite. */
	private groupsByRoot = new Map<OptionId, FormulaOrGroup[]>();

	private constructor(
		private readonly response: MotornetAccessoriesResponse,
		public readonly vehiclePrice: number
	) {
		this.rebuild();
	}

	/**
	 * Crea un engine per un payload accessori e un prezzo veicolo. Async per
	 * simmetria con eventuali implementazioni che caricano dati in modo pigro.
	 */
	static async create(
		response: MotornetAccessoriesResponse,
		vehiclePrice = 0
	): Promise<MotornetEngine> {
		return new MotornetEngine(response, vehiclePrice);
	}

	/**
	 * Ricostruisce l'intera configurazione dai `picks`: riparte dagli accessori
	 * grezzi e riapplica in ordine ogni catena. È deliberatamente un ricalcolo
	 * completo — nessuno stato incrementale sopravvive tra le mutazioni.
	 */
	private rebuild(): void {
		this.working = flattenAccessories(this.response);
		const byId = new Map(this.working.map((a) => [a.idAccessorio, a]));

		// nulla è pre-rivendicato: capostipiti e compagni rivendicano i propri
		// target man mano che le catene si applicano, così un optional può
		// escludere (o trascinare) un accessorio di serie
		const claimed = new Set<number>();

		// i pick si applicano nell'ordine di selezione: la prima catena vince i target contesi
		for (const id of this.picks) {
			const accessory = byId.get(id);
			if (!accessory || accessory.tipoAccessorio === 'serie') continue;
			selectAccessoryWithChoices(accessory, this.working, this.choices, claimed);
		}

		this.groupsByRoot = this.collectOrGroups();
	}

	/**
	 * Domande (nodi OR) di ogni catena applicata, indicizzate per pick capostipite.
	 * I compagni contribuiscono con le proprie domande: i compagni di vincolo con
	 * tutte e tre le formule, i compagni di inclusione solo con le loro esclusioni
	 * (coerentemente con ciò che la catena effettivamente valuta per essi).
	 */
	private collectOrGroups(): Map<OptionId, FormulaOrGroup[]> {
		const out = new Map<OptionId, FormulaOrGroup[]>();
		const pickSet = new Set(this.picks);

		for (const a of this.working) {
			if (!a.selected) continue;
			const rootId = a.appliedBy ?? a.idAccessorio;
			if (!pickSet.has(rootId)) continue;

			const full = a.appliedKind !== 'inclusione';
			// i capostipiti di catena sono `isFirstOfChain` per il wizard ufficiale:
			// non vengono mai proposti come risposta a una domanda sollevata dalla
			// loro stessa catena
			const groups: FormulaOrGroup[] = [
				...(full
					? collectFormulaOrGroups(
							a,
							'vincolo',
							a.formulaVincoliStrutturata,
							this.working,
							this.choices,
							pickSet
					  )
					: []),
				...(full
					? collectFormulaOrGroups(
							a,
							'inclusione',
							a.formulaInclusioneStrutturata,
							this.working,
							this.choices,
							pickSet
					  )
					: []),
				...collectFormulaOrGroups(
					a,
					'esclusione',
					a.formulaEsclusioneStrutturata,
					this.working,
					this.choices,
					pickSet
				),
			];
			if (groups.length === 0) continue;
			out.set(rootId, [...(out.get(rootId) ?? []), ...groups]);
		}

		return out;
	}

	/** Cerca un accessorio di lavoro per idAccessorio. */
	private byId(id: OptionId): WorkingAccessory | undefined {
		return this.working.find((a) => a.idAccessorio === id);
	}

	/** Mappa id → effetto (vincolo/inclusione/esclusione) dell'attuale configurazione. */
	private vincoloMap(): Record<string, MotornetTipoVincolo> {
		return collectVincoloMap(this.working);
	}

	/**
	 * Seleziona un accessorio. Gli accessori di serie sono ignorati. Prima di
	 * aggiungere il pick risolve i conflitti "l'ultima scelta vince" rimuovendo i
	 * pick mutuamente esclusivi, poi ricostruisce.
	 */
	async select(id: OptionId): Promise<ConfigState> {
		const accessory = this.byId(id);
		if (!accessory || accessory.tipoAccessorio === 'serie') return this.state();

		const excluded = this.mutuallyExcluded(id);
		this.picks = this.picks.filter((p) => p !== id && !excluded.has(p));
		this.picks.push(id);
		this.rebuild();
		return this.state();
	}

	/** Deseleziona un accessorio: lo rimuove dai pick, ne ripulisce lo stato e ricostruisce. */
	async deselect(id: OptionId): Promise<ConfigState> {
		this.picks = this.picks.filter((p) => p !== id);
		const accessory = this.byId(id);
		if (accessory) deselectAccessory(accessory);
		this.rebuild();
		return this.state();
	}

	/** Imposta le risposte ai nodi OR e ricostruisce la configurazione. */
	async setChoices(choices: OrChoice[]): Promise<ConfigState> {
		this.choices = choices;
		this.rebuild();
		return this.state();
	}

	/**
	 * Pre-risoluzione "l'ultima scelta vince" (Q&A #11): i pick la cui catena
	 * esclude il candidato — o che la catena del candidato escluderebbe — vengono
	 * rimossi prima che il nuovo pick venga applicato. Restituisce l'insieme dei
	 * pick da eliminare.
	 */
	private mutuallyExcluded(id: OptionId): Set<OptionId> {
		const out = new Set<OptionId>();
		const pickSet = new Set(this.picks);

		for (const s of this.working) {
			if (!s.selected) continue;
			if (s.tipoVincoloArray?.[id] !== 'esclusione') continue;
			const root = s.appliedBy ?? s.idAccessorio;
			if (pickSet.has(root)) out.add(root);
		}

		// simula, su una copia isolata, la catena del candidato per scoprire quali
		// pick già presenti verrebbero da esso esclusi
		const probe = flattenAccessories(this.response);
		const probed = probe.find((a) => a.idAccessorio === id);
		if (probed && probed.tipoAccessorio !== 'serie') {
			const claimed = new Set<number>();
			selectAccessoryWithChoices(probed, probe, this.choices, claimed);
			const stamps = collectVincoloMap(probe);
			for (const pick of this.picks) {
				if (stamps[pick] === 'esclusione') out.add(pick);
			}
		}

		return out;
	}

	/**
	 * Dominio di ogni accessorio rispetto alla configurazione corrente: `forced`
	 * se scelto o imposto da un vincolo/inclusione, `forbidden` se escluso,
	 * `available` altrimenti. Alimenta lo stato abilitato/disabilitato in UI.
	 */
	async validDomains(): Promise<Map<OptionId, Domain>> {
		const map = this.vincoloMap();
		const picked = new Set(this.picks);
		const out = new Map<OptionId, Domain>();

		for (const accessory of this.working) {
			const id = accessory.idAccessorio;
			if (picked.has(id)) {
				out.set(id, 'forced');
				continue;
			}
			const stamp = map[id];
			if (stamp === 'esclusione') out.set(id, 'forbidden');
			else if (stamp) out.set(id, 'forced');
			else out.set(id, 'available');
		}

		return out;
	}

	/** Accessori selezionati che impongono `id` (per vincolo o inclusione). */
	async forcedBy(id: OptionId): Promise<OptionId[]> {
		return this.stampedBy(id, (k) => k === 'vincolo' || k === 'inclusione');
	}

	/** Accessori selezionati che escludono `id`. */
	async blockedBy(id: OptionId): Promise<OptionId[]> {
		return this.stampedBy(id, (k) => k === 'esclusione');
	}

	/** Tutto ciò che la catena con capostipite `id` marca come escluso. */
	excludes(id: OptionId): OptionId[] {
		const out = new Set<OptionId>();
		for (const a of this.working) {
			if (!a.selected) continue;
			if (a.idAccessorio !== id && a.appliedBy !== id) continue;
			for (const [key, kind] of Object.entries(a.tipoVincoloArray ?? {})) {
				if (kind !== 'esclusione') continue;
				const target = Number(key);
				if (Number.isInteger(target)) out.add(target);
			}
		}
		return [...out];
	}

	/**
	 * Accessori selezionati il cui effetto su `id` soddisfa il predicato `match`:
	 * base condivisa da {@link forcedBy} e {@link blockedBy}.
	 */
	private stampedBy(
		id: OptionId,
		match: (kind: MotornetTipoVincolo) => boolean
	): OptionId[] {
		const out: OptionId[] = [];
		for (const accessory of this.working) {
			if (!accessory.selected) continue;
			const kind = accessory.tipoVincoloArray?.[id];
			if (kind && match(kind)) out.push(accessory.idAccessorio);
		}
		return out;
	}

	/**
	 * True se il pick `id` può essere rimosso: dev'essere un pick esplicito e non
	 * essere imposto da nessun altro accessorio selezionato.
	 */
	async canRemove(id: OptionId): Promise<boolean> {
		if (!this.picks.includes(id)) return false;
		const holders = (await this.forcedBy(id)).filter((h) => h !== id);
		return holders.length === 0;
	}

	/** Domande OR (aperte e risposte) della catena con capostipite il pick `id`. */
	orGroups(id: OptionId): FormulaOrGroup[] {
		return this.groupsByRoot.get(id) ?? [];
	}

	/** Istantanea corrente: pick, configurazione completa e totali (lordo/netto). */
	async state(): Promise<ConfigState> {
		const configuration = this.working
			.filter((a) => a.selected && a.tipoAccessorio !== 'serie')
			.map((a) => a.idAccessorio)
			.sort((a, b) => a - b);

		return {
			picks: [...this.picks],
			configuration,
			price: {
				retail: totalAccessoriesPrice(this.working, 'prezzoListino'),
				base: totalAccessoriesPrice(this.working, 'prezzoListinoNetto'),
			},
		};
	}

	/** `idDizionario` (deduplicati) dei pick: il formato con cui il BE identifica gli accessori. */
	wireIds(): number[] {
		const out: number[] = [];
		for (const id of this.picks) {
			const accessory = this.byId(id);
			if (accessory) out.push(accessory.idDizionario);
		}
		return [...new Set(out)];
	}

	/**
	 * Dettaglio prezzo per riga degli accessori selezionati (esclusi i serie). I
	 * compagni di inclusione compaiono a costo 0; per gli altri vale il prezzo da
	 * vincolo se presente, altrimenti il listino proprio. `requiredBy` indica chi
	 * ha trascinato l'accessorio.
	 */
	priceBreakdown(): {
		lines: PriceLine[];
		total: { retail: number; base: number };
	} {
		const lines: PriceLine[] = [];

		for (const accessory of this.working) {
			if (!accessory.selected) continue;
			if (accessory.tipoAccessorio === 'serie') continue;

			const included = accessory.appliedKind === 'inclusione';
			lines.push({
				id: accessory.idAccessorio,
				code: accessory.codice,
				retail: included
					? 0
					: accessory.prezzoDaVincolo ?? accessory.prezzoListino ?? 0,
				base: included
					? 0
					: accessory.prezzoDaVincoloNetto ?? accessory.prezzoListinoNetto ?? 0,
				requiredBy: accessory.appliedBy ?? null,
			});
		}

		return {
			lines,
			total: {
				retail: lines.reduce((s, l) => s + l.retail, 0),
				base: lines.reduce((s, l) => s + l.base, 0),
			},
		};
	}
}
